import React, { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { useScadaWS } from "../lib/useScadaWS";
import { Activity, AlertTriangle, Cpu, Bell, Zap } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";

const STAT_CARDS = [
  { key: "tag_count", label: "Total Tags", icon: Activity, color: "#00E5FF" },
  { key: "device_count", label: "Devices", icon: Cpu, color: "#00E5FF" },
  { key: "active_alarms", label: "Active Alarms", icon: Bell, color: "#FFB300" },
  { key: "critical_alarms", label: "Critical", icon: AlertTriangle, color: "#FF3B30" },
];

function TagCard({ tag }) {
  const [history, setHistory] = useState([]);
  const prevValue = useRef(tag.current_value);
  const [flash, setFlash] = useState(false);
 
  useEffect(() => {
    if (prevValue.current !== tag.current_value) {
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 350);
      prevValue.current = tag.current_value;
      setHistory((h) => [...h.slice(-29), { v: tag.current_value }]);
      return () => clearTimeout(t);
    }
  }, [tag.current_value]);
 
  const color = tag.current_status === "critical" ? "#FF3B30"
    : tag.current_status === "warning" ? "#FFB300" : "#00E676";
 
  const statusClass = `status-${tag.current_status || "normal"}`;
  const pulseClass = tag.current_status === "critical" ? "alarm-critical-pulse" : "";
 
  return (
    <div className={`scada-panel p-4 relative overflow-hidden ${statusClass} ${pulseClass} ${flash ? "value-flash" : ""}`} data-testid={`tag-card-${tag.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="scada-label truncate text-[9px]">{tag.name}</div>
          <div className="text-[9px] text-[#5A6573] font-mono-metric uppercase mt-0.5 tracking-wider">{tag.tag_type}</div>
        </div>
        <div className="relative flex h-2 w-2 mt-1">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: color }}></span>
          <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}></span>
        </div>
      </div>
      <div className="flex items-end justify-between mt-3">
        <div>
          <div 
            className="font-mono-metric text-3xl font-bold tracking-tight" 
            style={{ color: tag.current_status ? color : "#E3E8EE", textShadow: tag.current_status ? `0 0 8px ${color}60` : "none" }}
            data-testid={`tag-value-${tag.id}`}
          >
            {tag.tag_type === "digital" ? (tag.current_value >= 0.5 ? "ON" : "OFF") : tag.current_value?.toFixed?.(2) ?? "—"}
          </div>
          <div className="text-[9px] text-[#9BA4B5] font-mono-metric mt-1">{tag.unit}</div>
        </div>
        <div className="h-10 w-24 -mr-2">
          {history.length > 1 && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={history}>
                <defs>
                  <linearGradient id={`g-${tag.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={color} stopOpacity={0.4} />
                    <stop offset="100%" stopColor={color} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Area dataKey="v" stroke={color} fill={`url(#g-${tag.id})`} strokeWidth={1.5} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [tags, setTags] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");

  useEffect(() => {
    api.get("/sites").then(({ data }) => {
      setSites(data);
      if (data[0]) setSiteId(data[0].id);
    });
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/dashboard/summary", { params: siteId ? { site_id: siteId } : {} }),
          api.get("/tags", { params: siteId ? { site_id: siteId } : {} }),
        ]);
        if (!mounted) return;
        setSummary(s.data);
        setTags(t.data);
      } catch (e) { /* */ }
    };
    load();
    const iv = setInterval(load, 5000);   // reconcile every 5s; WS handles per-tag updates
    return () => { mounted = false; clearInterval(iv); };
  }, [siteId]);

  // Live tag updates via WebSocket
  useScadaWS((msg) => {
    if (msg.type === "tag_update") {
      if (siteId && msg.site_id !== siteId) return;
      setTags((prev) => prev.map(t => t.id === msg.tag_id ? { ...t, current_value: msg.value, current_status: msg.status, last_update: msg.ts } : t));
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="scada-label">CONTROL DASHBOARD</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">Live Operations</h1>
        </div>
        {sites.length > 1 && (
          <select
            value={siteId}
            onChange={(e) => setSiteId(e.target.value)}
            data-testid="site-select"
            className="border px-3 py-2 text-sm font-mono-metric"
          >
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name} — {s.customer}</option>)}
          </select>
        )}
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
        {STAT_CARDS.map((c) => (
          <div key={c.key} className="scada-panel p-5 relative overflow-hidden" data-testid={`stat-${c.key}`}>
            <div className="absolute top-0 left-0 w-8 h-[1px]" style={{ backgroundColor: c.color }} />
            <div className="absolute top-0 left-0 w-[1px] h-8" style={{ backgroundColor: c.color }} />
            <div className="flex items-center justify-between">
              <div className="scada-label text-[9px]">{c.label}</div>
              <c.icon size={14} style={{ color: c.color }} />
            </div>
            <div 
              className="font-mono-metric text-4xl mt-3 font-bold tracking-tight" 
              style={{ color: c.color, textShadow: `0 0 10px ${c.color}50` }}
            >
              {summary?.[c.key] ?? "—"}
            </div>
          </div>
        ))}
      </div>

      {/* Tag grid */}
      <div>
        <div className="flex items-center gap-3 mb-3">
          <Zap className="text-[#00E5FF]" size={14} />
          <div className="scada-label">REAL-TIME TAG STREAM</div>
          <div className="flex-1 h-px bg-[#2C3238]" />
          <div className="font-mono-metric text-[10px] text-[#5A6573]">{tags.length} TAGS</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-1">
          {tags.map((t) => <TagCard key={t.id} tag={t} />)}
        </div>
      </div>
    </div>
  );
}
